'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import useSWR from 'swr';
import Link from 'next/link';
import { useAuth } from '../lib/auth';
import { apiFetch, API_BASE } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { helpLang, HelpPagesResponse } from '../lib/help';
import { useDraggable } from '../lib/useDraggable';
import { useExitAnimation } from '../lib/useExitAnimation';
import { LegacyPageContent, normalizeLegacyContent } from '../lib/pageBlocks';
import PageRenderer, { extractTocHeadings } from './PageRenderer';
import PageBlocks from './PageBlocks';
import HelpToc from './HelpToc';
import { useMemo } from 'react';

type HelpDrawerApi = {
  openHelp: (slug: string, anchor?: string) => void;
};

const HelpDrawerContext = createContext<HelpDrawerApi | null>(null);

export function useHelpDrawer() {
  return useContext(HelpDrawerContext);
}

/**
 * Postranní panel nápovědy — článek se otevře jako scrollovatelný drawer
 * vpravo místo nového tabu. Stránka pod ním zůstává viditelná; panel
 * zavřeš ✕, ESC nebo klikem na pozadí. ↗ otevře článek jako celou stránku.
 */
export function HelpDrawerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ slug: string; anchor?: string } | null>(null);
  const openHelp = useCallback((slug: string, anchor?: string) => setState({ slug, anchor }), []);
  const close = useCallback(() => setState(null), []);
  // Exit animace — drawer má vlastní slide transition (200 ms), držet mount déle
  const exiting = useExitAnimation(state != null, 240);
  const [lastState, setLastState] = useState(state);
  if (state && state !== lastState) setLastState(state);
  const drawer = state ?? lastState;

  return (
    <HelpDrawerContext.Provider value={{ openHelp }}>
      {children}
      {(state || exiting) && drawer && <HelpDrawer slug={drawer.slug} anchor={drawer.anchor} onClose={close} closing={!state} />}
    </HelpDrawerContext.Provider>
  );
}

function HelpDrawer({ slug, anchor, onClose, closing = false }: { slug: string; anchor?: string; onClose: () => void; closing?: boolean }) {
  const { isAuthenticated, mounted } = useAuth();
  const { t, locale } = useTranslation();
  const lang = helpLang(locale);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const drag = useDraggable();

  const { data, error, isLoading } = useSWR<unknown>(
    mounted && isAuthenticated && slug ? `/api/user/help/${lang}/pages/${slug}` : null,
    (url: string) => apiFetch<unknown>(url),
  );
  const { data: pagesData } = useSWR<HelpPagesResponse>(
    mounted && isAuthenticated ? `/api/user/help/${lang}/pages` : null,
  );
  const title = pagesData?.pages.find((p) => p.slug === slug)?.title || slug;
  const toc = useMemo(() => extractTocHeadings(data), [data]);

  // Slide-in animace
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Zamknout scroll stránky pod panelem
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC zavře panel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Scroll na anchor po načtení obsahu
  useEffect(() => {
    if (!anchor || data == null || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`#${CSS.escape(anchor)}`);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [anchor, data]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[10010] bg-black/20 transition-opacity duration-200 ${visible && !closing ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={t('menuNapoveda')}
        className={`fixed top-0 right-0 z-[10011] h-screen w-full sm:w-[480px] bg-linen border-l border-wheat shadow-2xl flex flex-col ${drag.pos ? '' : 'transition-transform duration-200 ease-out'}`}
        style={{
          transform: `translateX(calc(${visible && !closing ? '0%' : '100%'} + ${drag.pos?.x ?? 0}px)) translateY(${drag.pos?.y ?? 0}px)`,
        }}
      >
        <div
          className="flex items-center gap-2 px-4 py-3 border-b border-wheat shrink-0 cursor-move select-none"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('a, button')) return;
            drag.onMouseDown(e);
          }}
        >
          <span className="text-lg">📚</span>
          <h2 className="flex-1 min-w-0 text-base font-semibold text-bark truncate">{title}</h2>
          <Link
            href={`/help/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-wood/60 hover:text-moss-dark text-sm transition px-1"
            title={t('napovedaOpenPage')}
            aria-label={t('napovedaOpenPage')}
          >
            ↗
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 inline-flex items-center justify-center rounded-full text-wood/60 hover:bg-wheat/40 hover:text-bark transition"
            aria-label={t('close')}
          >
            ✕
          </button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 help-article">
          {isLoading && (
            <div className="flex justify-center py-12">
              <span className="inline-block w-6 h-6 border-2 border-ochre border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {error && <p className="text-sm text-rust-dark">{t('napovedaNotFound')}</p>}
          {data != null && toc.length >= 3 && (
            <details className="mb-4 border border-wheat rounded-md bg-cream/60 px-3 py-2">
              <summary className="text-sm font-semibold text-wood cursor-pointer select-none">{t('napovedaToc')}</summary>
              <div className="pt-2">
                <HelpToc headings={toc} hideLabel />
              </div>
            </details>
          )}
          {data != null && (
            Array.isArray((data as { sections?: unknown }).sections) ? (
              <PageRenderer content={data} apiBase={API_BASE} linkBase="/help" />
            ) : (
              <PageBlocks
                content={normalizeLegacyContent(data as LegacyPageContent)}
                apiBase={API_BASE}
                moreInfoLabel={t('pageBlocksMoreInfo')}
                findOutMoreLabel={t('pageBlocksFindOutMore')}
              />
            )
          )}
        </div>
      </aside>
    </>
  );
}
